import type { PaymentMethods } from '../../codegen/types';
import type { AdvancedPaymentMethodsLazyQueryHookResult } from '../../graphqlDocument/payments/advancedPaymentMethods.generated';
import { newRelicAvailabilityChecker } from '../newrelic';

interface GetExpressPaymentMethodsParams {
  getAdvancedPaymentMethods: AdvancedPaymentMethodsLazyQueryHookResult[0];
  cartId: string;
  ctLocale: string;
  currency: string;
  total: number;
}

interface ExpressPaymentMethods {
  applePay: PaymentMethods | null;
  googlePay: PaymentMethods | null;
}

export const getExpressPaymentMethods = async ({
  getAdvancedPaymentMethods,
  cartId,
  ctLocale,
  currency,
  total,
}: GetExpressPaymentMethodsParams): Promise<ExpressPaymentMethods> => {
  try {
    const { data } = await getAdvancedPaymentMethods({
      variables: {
        input: {
          cartId,
          locale: ctLocale,
          amount: {
            currency,
            value: total,
          },
        },
      },
      fetchPolicy: 'no-cache',
    });

    const { paymentMethods } = data?.advancedPaymentMethods ?? {};

    const applePay =
      paymentMethods?.find(
        (paymentMethod): paymentMethod is PaymentMethods => paymentMethod?.type?.toLowerCase() === 'applepay',
      ) ?? null;

    const googlePay =
      paymentMethods?.find(
        (paymentMethod): paymentMethod is PaymentMethods => paymentMethod?.type?.toLowerCase() === 'googlepay',
      ) ?? null;

    return { applePay, googlePay };
  } catch (error) {
    console.error('[APX][GPX] Error fetching advanced payment methods:', error);

    newRelicAvailabilityChecker(() => {
      window.newrelic.noticeError(new Error(`[APX][GPX] Error fetching advanced payment methods: ${error}`));
    });

    return { applePay: null, googlePay: null };
  }
};
